(() => {
  const SELECTOR = '[data-calinfi-product-hero]';
  const PREORDER_VIEW_BY_PATH = {
    '/products/odun': 'odun-launch-v6',
    '/products/odun-ls': 'odun-ls-v6',
    '/products/odun-s': 'odun-s-preorder-v6',
  };
  const PREORDER_TERMS_URL = '/pages/preorder-terms';

  function ensurePreorderView() {
    const params = new URLSearchParams(window.location.search);

    // a) Do not redirect if a view is already set.
    if (params.has('view')) return;

    // b) Only redirect for known ODUN handles.
    const path = window.location.pathname.replace(/\/$/, '');
    const preorderView = PREORDER_VIEW_BY_PATH[path];
    if (!preorderView) return;

    params.set('view', preorderView);
    window.location.replace(`${path}?${params.toString()}${window.location.hash}`);
  }

  function installCartDrawerPreviewGuard() {
    const patchDrawers = () => {
      try {
        document.querySelectorAll('cart-drawer').forEach((drawer) => patchCartDrawer(drawer));
      } catch (e) {
        console.warn('[CALINFI] Cart drawer guard skipped:', e.message);
      }
    };

    try {
      if (window.customElements?.whenDefined) {
        window.customElements.whenDefined('cart-drawer').then(patchDrawers).catch(() => {});
      }
    } catch (e) {
      console.warn('[CALINFI] Cart drawer whenDefined skipped:', e.message);
    }

    patchDrawers();
  }

  function patchCartDrawer(drawer) {
    if (!drawer || drawer.dataset.calinfiCartPreviewGuard === 'true') return;

    try {
      drawer.dataset.calinfiCartPreviewGuard = 'true';

      if (typeof drawer.handlePurchasePreviewUpdate === 'function') {
        document.removeEventListener('calinfi:product-purchase:update', drawer.handlePurchasePreviewUpdate);
      }

      drawer.selectionPreviewState = null;
      drawer.requestPurchasePreviewState = () => {};
      drawer.handlePurchasePreviewUpdate = () => removeCartDrawerPreview(drawer);
      drawer.renderSelectionPreview = () => removeCartDrawerPreview(drawer);
    } catch (e) {
      console.warn('[CALINFI] Cart drawer patch skipped:', e.message);
    }

    try {
      if (typeof drawer.open === 'function') {
        const originalOpen = drawer.open.bind(drawer);
        drawer.open = (triggeredBy) => {
          removeCartDrawerPreview(drawer);
          const result = originalOpen(triggeredBy);
          removeCartDrawerPreview(drawer);
          return result;
        };
      }
    } catch (e) {
      console.warn('[CALINFI] Cart drawer open patch skipped:', e.message);
    }

    try {
      if (typeof drawer.renderContents === 'function') {
        const originalRenderContents = drawer.renderContents.bind(drawer);
        drawer.renderContents = (parsedState) => {
          const result = originalRenderContents(parsedState);
          window.setTimeout(() => removeCartDrawerPreview(drawer));
          return result;
        };
      }
    } catch (e) {
      console.warn('[CALINFI] Cart drawer renderContents patch skipped:', e.message);
    }

    try {
      removeCartDrawerPreview(drawer);
    } catch (e) {
      console.warn('[CALINFI] Cart drawer cleanup skipped:', e.message);
    }
  }

  function removeCartDrawerPreview(drawer) {
    drawer.querySelector('[data-calinfi-cart-selection-preview]')?.remove();
    drawer.classList.remove('has-selection-preview');

    const summary = drawer.querySelector('.calinfi-cart-drawer__summary');
    if (summary?.dataset.cartSummary) {
      summary.textContent = summary.dataset.cartSummary;
      delete summary.dataset.cartSummary;
    }
  }

  let lastMobileKitSummaryToggleAt = 0;

  function getMobileSummaryTapRoot(event) {
    if (!window.matchMedia('(max-width: 749px)').matches) return null;

    const target = event.target instanceof Element ? event.target : null;
    if (!target) return null;

    const root = target.closest(SELECTOR);
    if (!root) return null;

    if (
      target.closest(
        '.calinfi-product-hero__actions, .calinfi-product-hero__mobile-kit-panel, .calinfi-product-hero__payment-modal'
      )
    ) {
      return null;
    }

    if (target.closest('[data-calinfi-mobile-kit-toggle], [data-calinfi-total]')) return root;

    const purchaseBar = root.querySelector('.calinfi-product-hero__purchase');
    if (!purchaseBar) return null;

    const point = event.changedTouches?.[0] || event.touches?.[0] || event;
    const clientX = point.clientX;
    const clientY = point.clientY;
    if (typeof clientX !== 'number' || typeof clientY !== 'number') return null;

    const rect = purchaseBar.getBoundingClientRect();
    const isInsidePurchaseBar =
      clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom;
    const isSummaryZone = clientX - rect.left <= rect.width * 0.42;

    return isInsidePurchaseBar && isSummaryZone ? root : null;
  }

  function installMobileKitSummaryFallback() {
    if (window.__calinfiMobileKitSummaryFallbackInstalled) return;
    window.__calinfiMobileKitSummaryFallbackInstalled = true;

    const handler = (event) => {
      const root = getMobileSummaryTapRoot(event);
      const controller = root?.__calinfiProductHero;
      if (!controller?.toggleMobileKitSummary) return;

      const now = Date.now();
      if (now - lastMobileKitSummaryToggleAt < 320) return;
      lastMobileKitSummaryToggleAt = now;

      if (event.cancelable) event.preventDefault();
      event.stopPropagation();
      controller.toggleMobileKitSummary();
    };

    document.addEventListener('touchend', handler, { capture: true, passive: false });
    document.addEventListener('pointerup', handler, { capture: true });
  }

  class CalinfiProductHero {
    constructor(root) {
      this.root = root;
      this.root.__calinfiProductHero = this;
      this.sectionId = root.dataset.sectionId;
      this.moneyFormat = root.dataset.moneyFormat || '${{amount}}';
      this.currentVariantId = Number(root.dataset.currentVariantId || 0);
      this.currentVariantPrice = Number(root.dataset.currentVariantPrice || 0);
      this.currentVariantAvailable = root.dataset.currentVariantAvailable === 'true';
      this.currentSellingPlanId = root.dataset.currentSellingPlanId || '';
      this.fullPaymentPlanByVariant = this.readJsonData('[data-calinfi-full-payment-plans]');
      const initialFullPaymentPlan = this.fullPaymentPlanByVariant[String(this.currentVariantId)] || {};
      this.fullPaymentSellingPlanId = String(
        initialFullPaymentPlan.sellingPlanId || root.dataset.currentFullPaymentSellingPlanId || ''
      );
      this.currentFullPaymentPrice = Number(initialFullPaymentPlan.price ?? this.currentVariantPrice);
      this.media = Array.from(root.querySelectorAll('[data-calinfi-product-media]'));
      this.currentMediaIndex = Math.max(0, this.media.findIndex((item) => item.classList.contains('is-active')));
      this.galleryCount = root.querySelector('[data-calinfi-gallery-count]');
      this.prevGalleryButton = root.querySelector('[data-calinfi-gallery-prev]');
      this.nextGalleryButton = root.querySelector('[data-calinfi-gallery-next]');
      this.filterButtons = Array.from(root.querySelectorAll('[data-calinfi-accessory-filter]'));
      this.accessoryScroll = root.querySelector('.calinfi-product-hero__accessory-scroll');
      this.accessoryList = root.querySelector('[data-calinfi-accessory-list]');
      this.accessoryScrollbar = root.querySelector('[data-calinfi-accessory-scrollbar]');
      this.accessoryScrollThumb = root.querySelector('[data-calinfi-accessory-scroll-thumb]');
      this.accessoryCards = Array.from(root.querySelectorAll('[data-calinfi-accessory]'));
      this.versionLinks = Array.from(root.querySelectorAll('.calinfi-product-hero__version-link[href]'));
      this.totalElement = root.querySelector('[data-calinfi-total]');
      this.purchaseBar = root.querySelector('.calinfi-product-hero__purchase');
      this.errorElement = root.querySelector('[data-calinfi-product-error]');
      this.addToCartButton = root.querySelector('[data-calinfi-add-to-cart]');
      this.buyNowButton = root.querySelector('[data-calinfi-buy-now]');
      this.mobileKitToggle = root.querySelector('[data-calinfi-mobile-kit-toggle]');
      this.mobileKitLabel = root.querySelector('[data-calinfi-mobile-kit-label]');
      this.mobileKitPanel = root.querySelector('[data-calinfi-mobile-kit-panel]');
      this.mobileKitList = root.querySelector('[data-calinfi-mobile-kit-list]');
      this.mobileKitClose = root.querySelector('[data-calinfi-mobile-kit-close]');
      this.paymentModal = root.querySelector('[data-calinfi-payment-modal]');
      this.paymentDialog = root.querySelector('.calinfi-product-hero__payment-dialog');
      this.paymentContinueButton = root.querySelector('[data-calinfi-payment-continue]');
      this.paymentContinueLabel = root.querySelector('[data-calinfi-payment-continue-label]');
      this.paymentTermsLink = root.querySelector('.calinfi-product-hero__payment-terms a');
      this.paymentCloseButtons = Array.from(root.querySelectorAll('[data-calinfi-payment-close]'));
      this.paymentError = root.querySelector('[data-calinfi-payment-error]');
      this.paymentAmountElements = Array.from(root.querySelectorAll('[data-calinfi-payment-amount]'));
      this.pendingPurchaseAction = null;
      this.previouslyFocusedBeforePayment = null;
      this.productInfo = root.closest('product-info');
      this.activeElement = null;
      this.variantChangeUnsubscriber = null;
    }

    init() {
      if (this.root.dataset.calinfiProductHeroInitialized === 'true') return;
      this.root.dataset.calinfiProductHeroInitialized = 'true';

      this.prevGalleryButton?.addEventListener('click', () => this.showMedia(this.currentMediaIndex - 1));
      this.nextGalleryButton?.addEventListener('click', () => this.showMedia(this.currentMediaIndex + 1));

      this.filterButtons.forEach((button) => {
        button.addEventListener('click', () => this.setActiveCategory(button.dataset.calinfiAccessoryFilter));
      });

      this.versionLinks.forEach((link) => {
        link.addEventListener('click', (event) => this.handleVersionLinkClick(event));
      });

      this.accessoryCards.forEach((card) => {
        const minus = card.querySelector('[data-calinfi-accessory-minus]');
        const plus = card.querySelector('[data-calinfi-accessory-plus]');
        const select = card.querySelector('[data-calinfi-accessory-select]');

        card.dataset.quantity = card.dataset.quantity || '0';
        card.addEventListener('click', (event) => {
          if (event.target.closest('a, button')) return;
          if (Number(card.dataset.quantity || 0) === 0) this.changeAccessoryQuantity(card, 1);
        });
        select?.addEventListener('click', () => {
          if (Number(card.dataset.quantity || 0) === 0) this.changeAccessoryQuantity(card, 1);
        });
        minus?.addEventListener('click', () => this.changeAccessoryQuantity(card, -1));
        plus?.addEventListener('click', () => this.changeAccessoryQuantity(card, 1));
      });

      this.accessoryScroll?.addEventListener('wheel', (event) => this.handleAccessoryWheel(event), { passive: false });
      this.accessoryList?.addEventListener('keydown', (event) => this.handleAccessoryKeydown(event));
      this.accessoryList?.addEventListener('scroll', () => this.updateAccessoryScrollbar(), { passive: true });
      window.addEventListener('resize', () => this.updateAccessoryScrollbar());

      this.paymentContinueButton?.addEventListener('click', () => this.continuePaymentChoice());

      this.paymentCloseButtons.forEach((button) => {
        button.addEventListener('click', () => this.closePaymentModal());
      });

      this.paymentModal?.addEventListener('keydown', (event) => this.handlePaymentModalKeydown(event));

      this.addToCartButton?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.activeElement = event.currentTarget;
        this.requestPaymentChoice({ checkout: false });
      });

      this.buyNowButton?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.activeElement = event.currentTarget;
        this.requestPaymentChoice({ checkout: true });
      });

      this.root.addEventListener('click', (event) => {
        const toggle = event.target.closest('[data-calinfi-mobile-kit-toggle], [data-calinfi-total]');
        if (toggle && this.root.contains(toggle)) {
          event.preventDefault();
          event.stopPropagation();
          this.toggleMobileKitSummary();
          return;
        }

        const close = event.target.closest('[data-calinfi-mobile-kit-close]');
        if (close && this.root.contains(close)) {
          event.preventDefault();
          event.stopPropagation();
          this.toggleMobileKitSummary(false);
        }
      });
      this.purchaseBar?.addEventListener('click', (event) => this.handleMobilePurchaseClick(event));
      this.mobileKitList?.addEventListener('click', (event) => this.handleMobileKitAction(event));

      document.addEventListener('calinfi:product-purchase:request', () => this.publishPurchaseState());

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event?.data?.sectionId !== this.sectionId) return;
          this.handleVariantChange(event.data.variant);
        });
      }

      this.showMedia(this.currentMediaIndex);
      this.updateCurrentSellingPlan();
      this.syncPaymentTermsLink();
      this.updateTotal();
      this.updateButtons();
      this.updateAccessoryScrollbar();
    }

    destroy() {
      this.variantChangeUnsubscriber?.();
    }

    showMedia(index) {
      if (!this.media.length) return;

      const normalizedIndex = (index + this.media.length) % this.media.length;
      this.currentMediaIndex = normalizedIndex;

      this.media.forEach((item, itemIndex) => {
        const isActive = itemIndex === normalizedIndex;
        item.classList.toggle('is-active', isActive);
        item.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });

      if (this.galleryCount) {
        this.galleryCount.textContent = `${normalizedIndex + 1}/${this.media.length}`;
      }
    }

    showMediaById(mediaId) {
      if (!mediaId) return;

      const mediaIndex = this.media.findIndex((item) => Number(item.dataset.mediaId) === Number(mediaId));
      if (mediaIndex >= 0) this.showMedia(mediaIndex);
    }

    setActiveCategory(category) {
      if (!category) return;

      this.filterButtons.forEach((button) => {
        const isActive = button.dataset.calinfiAccessoryFilter === category;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      this.accessoryCards.forEach((card) => {
        card.hidden = card.dataset.category !== category;
      });

      if (this.accessoryList) this.accessoryList.scrollTop = 0;
      this.updateAccessoryScrollbar();
    }

    updateAccessoryScrollbar() {
      if (!this.accessoryList || !this.accessoryScrollbar || !this.accessoryScrollThumb) return;

      window.requestAnimationFrame(() => {
        const maxScroll = this.accessoryList.scrollHeight - this.accessoryList.clientHeight;
        const trackHeight = this.accessoryScrollbar.clientHeight;
        const hasOverflow = maxScroll > 1 && trackHeight > 0;

        this.accessoryScrollbar.hidden = !hasOverflow;
        if (!hasOverflow) return;

        const thumbHeight = Math.max(40, Math.round(trackHeight * (this.accessoryList.clientHeight / this.accessoryList.scrollHeight)));
        const thumbTravel = Math.max(0, trackHeight - thumbHeight);
        const progress = this.accessoryList.scrollTop / maxScroll;

        this.accessoryScrollThumb.style.height = `${thumbHeight}px`;
        this.accessoryScrollThumb.style.transform = `translateY(${Math.round(thumbTravel * progress)}px)`;
      });
    }

    handleAccessoryWheel(event) {
      if (!this.accessoryList) return;

      const maxScroll = this.accessoryList.scrollHeight - this.accessoryList.clientHeight;
      if (maxScroll <= 1 || event.deltaY === 0) return;

      const isAtTop = this.accessoryList.scrollTop <= 0;
      const isAtBottom = this.accessoryList.scrollTop >= maxScroll - 1;
      if ((event.deltaY < 0 && isAtTop) || (event.deltaY > 0 && isAtBottom)) return;

      event.preventDefault();
      this.scrollAccessoryList(event.deltaY);
    }

    handleAccessoryKeydown(event) {
      const keyDistances = {
        ArrowDown: 42,
        ArrowUp: -42,
        PageDown: 180,
        PageUp: -180,
      };

      if (event.key === 'Home') {
        event.preventDefault();
        this.scrollAccessoryList(-Infinity);
        return;
      }

      if (event.key === 'End') {
        event.preventDefault();
        this.scrollAccessoryList(Infinity);
        return;
      }

      const distance = keyDistances[event.key];
      if (!distance) return;

      event.preventDefault();
      this.scrollAccessoryList(distance);
    }

    scrollAccessoryList(distance) {
      if (!this.accessoryList) return;

      const maxScroll = this.accessoryList.scrollHeight - this.accessoryList.clientHeight;
      const nextScrollTop = Math.min(maxScroll, Math.max(0, this.accessoryList.scrollTop + distance));
      this.accessoryList.scrollTop = nextScrollTop;
      this.updateAccessoryScrollbar();
    }

    changeAccessoryQuantity(card, delta) {
      if (!card || card.dataset.available !== 'true') return;

      const currentQuantity = Number(card.dataset.quantity || 0);
      const nextQuantity = Math.max(0, currentQuantity + delta);
      card.dataset.quantity = String(nextQuantity);
      card.classList.toggle('is-selected', nextQuantity > 0);

      const valueElement = card.querySelector('[data-calinfi-accessory-quantity]');
      if (valueElement) valueElement.textContent = String(nextQuantity);
      const selectButton = card.querySelector('[data-calinfi-accessory-select]');
      if (selectButton) selectButton.hidden = nextQuantity > 0;

      this.updateTotal();
    }

    resetAccessories() {
      this.accessoryCards.forEach((card) => {
        card.dataset.quantity = '0';
        card.classList.remove('is-selected');

        const valueElement = card.querySelector('[data-calinfi-accessory-quantity]');
        if (valueElement) valueElement.textContent = '0';
        const selectButton = card.querySelector('[data-calinfi-accessory-select]');
        if (selectButton) selectButton.hidden = false;
      });

      this.updateTotal();
    }

    toggleMobileKitSummary(force) {
      if (!this.mobileKitPanel) return;

      const isExpanded = typeof force === 'boolean' ? force : !this.root.classList.contains('is-mobile-kit-expanded');
      if (isExpanded) this.renderMobileKitPanel();
      this.root.classList.toggle('is-mobile-kit-expanded', isExpanded);
      this.mobileKitToggle?.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
      this.mobileKitPanel?.setAttribute('aria-hidden', isExpanded ? 'false' : 'true');
    }

    handleMobilePurchaseClick(event) {
      if (!window.matchMedia('(max-width: 749px)').matches) return;
      if (event.target.closest('.calinfi-product-hero__actions, .calinfi-product-hero__mobile-kit-panel')) return;
      if (event.target.closest('[data-calinfi-mobile-kit-toggle], [data-calinfi-total]')) return;

      const rect = this.purchaseBar?.getBoundingClientRect();
      if (!rect) return;

      const isSummaryZone = event.clientX - rect.left <= rect.width * 0.36;
      if (!isSummaryZone) return;

      event.preventDefault();
      event.stopPropagation();
      this.toggleMobileKitSummary();
    }

    handleMobileKitAction(event) {
      const button = event.target.closest('[data-calinfi-mobile-kit-action]');
      if (!button) return;

      const card = this.accessoryCards.find((item) => item.dataset.variantId === button.dataset.variantId);
      if (!card) return;

      const action = button.dataset.calinfiMobileKitAction;
      if (action === 'increase') this.changeAccessoryQuantity(card, 1);
      if (action === 'decrease') this.changeAccessoryQuantity(card, -1);
      if (action === 'remove') {
        card.dataset.quantity = '1';
        this.changeAccessoryQuantity(card, -1);
      }

      this.renderMobileKitPanel();
    }

    renderMobileKitPanel() {
      if (!this.mobileKitList) return;

      const productTitle = this.currentProductTitle();
      const activeImage = this.root.querySelector('.calinfi-product-hero__media.is-active img');
      const productImage = activeImage?.currentSrc || activeImage?.src || '';
      const rows = [
        {
          type: 'product',
          title: productTitle,
          subtitle: 'Current stove',
          price: this.currentVariantPrice,
          quantity: 1,
          image: productImage,
        },
        ...this.selectedAccessories().map((item) => ({
          type: 'accessory',
          id: item.id,
          title: item.title,
          subtitle: item.description,
          price: item.price,
          quantity: item.quantity,
          image: item.image,
        })),
      ];

      this.mobileKitList.innerHTML = rows.map((row) => this.mobileKitRowMarkup(row)).join('');
    }

    mobileKitRowMarkup(row) {
      const imageMarkup = row.image
        ? `<img class="calinfi-product-hero__mobile-kit-image" src="${this.escapeHtml(row.image)}" alt="">`
        : '<span class="calinfi-product-hero__mobile-kit-placeholder"></span>';
      const controlMarkup =
        row.type === 'accessory'
          ? `
            <div class="calinfi-product-hero__mobile-kit-controls" aria-label="${this.escapeHtml(row.title)} quantity">
              <button class="calinfi-product-hero__mobile-kit-button" type="button" data-calinfi-mobile-kit-action="increase" data-variant-id="${row.id}" aria-label="Increase ${this.escapeHtml(row.title)}">+</button>
              <span class="calinfi-product-hero__mobile-kit-quantity">${row.quantity}</span>
              <button class="calinfi-product-hero__mobile-kit-button" type="button" data-calinfi-mobile-kit-action="decrease" data-variant-id="${row.id}" aria-label="Decrease ${this.escapeHtml(row.title)}">&minus;</button>
              <button class="calinfi-product-hero__mobile-kit-button calinfi-product-hero__mobile-kit-button--remove" type="button" data-calinfi-mobile-kit-action="remove" data-variant-id="${row.id}" aria-label="Remove ${this.escapeHtml(row.title)}">
                <svg aria-hidden="true" width="13" height="15" viewBox="0 0 13 15" fill="none" focusable="false">
                  <path d="M11.4609 2.5L10.8428 10.5303C10.7821 11.3191 10.738 11.8818 10.6689 12.3223C10.6008 12.7568 10.5126 13.0321 10.3848 13.2529C10.1334 13.6869 9.75763 14.0355 9.30566 14.2529C9.07572 14.3635 8.79391 14.4302 8.35547 14.4648C7.91095 14.4999 7.34601 14.5 6.55469 14.5H6.44531C5.65399 14.5 5.08905 14.4999 4.64453 14.4648C4.20609 14.4302 3.92428 14.3635 3.69434 14.2529C3.24237 14.0355 2.86664 13.6869 2.61523 13.2529C2.48736 13.0321 2.39922 12.7568 2.33105 12.3223C2.26196 11.8818 2.2179 11.3191 2.15723 10.5303L1.53906 2.5H11.4609Z" stroke="currentColor"/>
                  <rect x="0.5" y="0.5" width="12" height="2" rx="0.5" stroke="currentColor"/>
                  <path d="M5 12L4.5 4" stroke="currentColor"/>
                  <path d="M8 12L8.5 4" stroke="currentColor"/>
                </svg>
              </button>
            </div>
          `
          : '';

      return `
        <article class="calinfi-product-hero__mobile-kit-item">
          <div class="calinfi-product-hero__mobile-kit-media" aria-hidden="true">
            ${imageMarkup}
          </div>
          <div class="calinfi-product-hero__mobile-kit-copy">
            <h3 class="calinfi-product-hero__mobile-kit-title">${this.escapeHtml(row.title)}</h3>
            ${row.subtitle ? `<p class="calinfi-product-hero__mobile-kit-text">${this.escapeHtml(row.subtitle)}</p>` : ''}
          </div>
          <p class="calinfi-product-hero__mobile-kit-price">${this.formatMoney(row.price)}</p>
          ${controlMarkup}
        </article>
      `;
    }

    handleVersionLinkClick(event) {
      if (!this.selectedAccessories().length) return;

      event.preventDefault();

      const link = event.currentTarget;
      const nextUrl = link?.href;
      const nextName = this.versionLinkTitle(link);

      this.requestKitSwitchChoice(nextName).then((choice) => {
        if (choice !== 'switch' || !nextUrl) return;

        this.resetAccessories();
        window.location.href = nextUrl;
      });
    }

    requestKitSwitchChoice(nextName) {
      return new Promise((resolve) => {
        const currentName = this.currentProductTitle();
        const previouslyFocused = document.activeElement;
        const modal = document.createElement('div');

        modal.className = 'calinfi-kit-conflict';
        modal.setAttribute('role', 'presentation');
        modal.innerHTML = `
          <div class="calinfi-kit-conflict__overlay" data-calinfi-kit-switch-close></div>
          <section
            class="calinfi-kit-conflict__dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="calinfi-kit-switch-title"
            aria-describedby="calinfi-kit-switch-copy"
            tabindex="-1"
          >
            <button class="calinfi-kit-conflict__close" type="button" data-calinfi-kit-switch-close aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
            <p class="calinfi-kit-conflict__eyebrow">Kit check</p>
            <h2 id="calinfi-kit-switch-title" class="calinfi-kit-conflict__title">
              Switching stove model will reset this kit.
            </h2>
            <p id="calinfi-kit-switch-copy" class="calinfi-kit-conflict__copy">
              Your current ${this.escapeHtml(currentName)} accessories are model-specific. Clear them and switch to ${this.escapeHtml(nextName)}?
            </p>
            <div class="calinfi-kit-conflict__actions">
              <button class="calinfi-kit-conflict__button calinfi-kit-conflict__button--primary" type="button" data-calinfi-kit-switch-action="switch">
                Clear & Switch
              </button>
              <button class="calinfi-kit-conflict__button calinfi-kit-conflict__button--secondary" type="button" data-calinfi-kit-switch-action="cancel">
                Keep Current Kit
              </button>
            </div>
          </section>
        `;

        const complete = (choice) => {
          document.removeEventListener('keydown', handleKeydown);
          modal.remove();
          document.body.classList.remove('calinfi-kit-conflict-open');
          previouslyFocused?.focus?.();
          resolve(choice);
        };

        const handleKeydown = (event) => {
          if (event.key === 'Escape') complete('cancel');
        };

        modal.addEventListener('click', (event) => {
          const action = event.target.closest('[data-calinfi-kit-switch-action]')?.dataset.calinfiKitSwitchAction;
          if (action) {
            complete(action);
            return;
          }

          if (event.target.closest('[data-calinfi-kit-switch-close]')) {
            complete('cancel');
          }
        });

        document.addEventListener('keydown', handleKeydown);
        document.body.append(modal);
        document.body.classList.add('calinfi-kit-conflict-open');
        window.requestAnimationFrame(() => {
          modal.querySelector('.calinfi-kit-conflict__dialog')?.focus();
        });
      });
    }

    versionLinkTitle(link) {
      const label = link?.getAttribute('aria-label') || link?.textContent || '';
      const normalizedLabel = label.replace(/^View\s+/i, '').trim();

      return normalizedLabel || 'the selected ODUN model';
    }

    handleVariantChange(variant) {
      if (!variant) {
        this.currentVariantId = 0;
        this.currentVariantAvailable = false;
        this.fullPaymentSellingPlanId = '';
        this.currentFullPaymentPrice = 0;
        this.updateCurrentSellingPlan();
        this.updateButtons();
        return;
      }

      this.currentVariantId = Number(variant.id);
      this.currentVariantPrice = Number(variant.price || 0);
      this.currentVariantAvailable = Boolean(variant.available);
      this.root.dataset.currentVariantId = String(this.currentVariantId);
      this.root.dataset.currentVariantPrice = String(this.currentVariantPrice);
      this.root.dataset.currentVariantAvailable = this.currentVariantAvailable ? 'true' : 'false';
      this.syncFullPaymentPlanForVariant(this.currentVariantId);
      this.updateCurrentSellingPlan();

      this.showMediaById(variant.featured_media?.id);
      this.updateTotal();
      this.updateButtons();
      this.clearError();
    }

    selectedAccessories() {
      return this.accessoryCards
        .map((card) => ({
          id: Number(card.dataset.variantId || 0),
          quantity: Number(card.dataset.quantity || 0),
          price: Number(card.dataset.fullPaymentPrice || card.dataset.price || 0),
          available: card.dataset.available === 'true',
          title: card.dataset.title || card.querySelector('.calinfi-product-hero__accessory-title')?.textContent?.trim() || '',
          description: card.querySelector('.calinfi-product-hero__accessory-text')?.textContent?.trim() || '',
          image: card.dataset.image || '',
          selling_plan: this.accessorySellingPlanId(card),
        }))
        .filter((item) => item.id && item.quantity > 0 && item.available);
    }

    requestPaymentChoice(action) {
      if (!this.isCurrentVariantSellable()) {
        this.showError(window.variantStrings?.unavailable || 'This product is unavailable.', { focus: true });
        return;
      }

      this.pendingPurchaseAction = action;
      this.clearError();
      this.clearPaymentError();

      if (!this.paymentModal) {
        this.addItems(action);
        return;
      }

      this.updateCurrentSellingPlan();
      this.updatePaymentSummary();
      this.updatePaymentContinueLabel(action);
      this.syncPaymentTermsLink();

      this.previouslyFocusedBeforePayment = document.activeElement;
      this.paymentModal.hidden = false;
      document.body.classList.add('calinfi-payment-modal-open');

      window.requestAnimationFrame(() => {
        this.paymentModal.classList.add('is-open');
        this.paymentDialog?.focus();
      });
    }

    continuePaymentChoice() {
      this.updateCurrentSellingPlan();

      if (!this.validatePaymentSelection()) return;

      const action = this.pendingPurchaseAction || { checkout: false };
      this.closePaymentModal({ restoreFocus: true, clearAction: false });
      this.pendingPurchaseAction = null;
      this.addItems(action);
    }

    closePaymentModal({ restoreFocus = true, clearAction = true } = {}) {
      if (!this.paymentModal || this.paymentModal.hidden) return;

      this.paymentModal.classList.remove('is-open');
      this.paymentModal.hidden = true;
      document.body.classList.remove('calinfi-payment-modal-open');
      this.clearPaymentError();

      if (clearAction) this.pendingPurchaseAction = null;
      if (restoreFocus) {
        const focusTarget = this.previouslyFocusedBeforePayment || this.activeElement;
        focusTarget?.focus?.();
      }

      this.previouslyFocusedBeforePayment = null;
    }

    handlePaymentModalKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closePaymentModal();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusableElements = this.focusableElements(this.paymentDialog);
      if (!focusableElements.length) {
        event.preventDefault();
        this.paymentDialog?.focus();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement;

      if (
        event.shiftKey &&
        (activeElement === firstElement || activeElement === this.paymentDialog || !this.paymentDialog?.contains(activeElement))
      ) {
        event.preventDefault();
        lastElement.focus();
        return;
      }

      if (
        !event.shiftKey &&
        (activeElement === lastElement || activeElement === this.paymentDialog || !this.paymentDialog?.contains(activeElement))
      ) {
        event.preventDefault();
        firstElement.focus();
      }
    }

    syncPaymentTermsLink() {
      if (!this.paymentTermsLink) return;

      this.paymentTermsLink.setAttribute('href', PREORDER_TERMS_URL);
    }

    updatePaymentSummary(total = this.totalPrice()) {
      if (!this.paymentAmountElements.length) return;

      const subtotal = this.isCurrentVariantSellable() ? Math.max(0, Number(total || 0)) : 0;
      const values = {
        'full-due': this.formatMoney(subtotal),
      };

      this.paymentAmountElements.forEach((element) => {
        const amountKey = element.dataset.calinfiPaymentAmount;
        if (!Object.prototype.hasOwnProperty.call(values, amountKey)) return;
        element.textContent = values[amountKey];
      });
    }

    updatePaymentContinueLabel(action = this.pendingPurchaseAction) {
      if (!this.paymentContinueButton || !this.paymentContinueLabel) return;

      const label = action?.checkout
        ? this.paymentContinueButton.dataset.checkoutLabel
        : this.paymentContinueButton.dataset.cartLabel;

      if (label) this.paymentContinueLabel.textContent = label;
    }

    showPaymentError(message, { focus = false } = {}) {
      if (!this.paymentError) return;
      this.paymentError.textContent = message || 'The full-payment preorder is not ready for this item.';
      this.paymentError.hidden = false;
      if (focus) window.requestAnimationFrame(() => this.paymentError?.focus());
    }

    clearPaymentError() {
      if (!this.paymentError) return;
      this.paymentError.textContent = '';
      this.paymentError.hidden = true;
    }

    selectedPaymentOption() {
      return 'full';
    }

    selectedPaymentLabel() {
      return 'Full payment preorder';
    }

    selectedSellingPlanId() {
      return this.fullPaymentSellingPlanId;
    }

    accessorySellingPlanId(card) {
      return card.dataset.fullPaymentSellingPlanId || card.dataset.sellingPlanId || '';
    }

    updateCurrentSellingPlan() {
      this.currentSellingPlanId = this.selectedSellingPlanId() || '';
      this.root.dataset.currentSellingPlanId = this.currentSellingPlanId;

      const sellingPlanInput = this.root.querySelector('input[name="selling_plan"]');
      if (sellingPlanInput) {
        sellingPlanInput.value = this.currentSellingPlanId;
        sellingPlanInput.disabled = !this.currentSellingPlanId;
      }
    }

    syncFullPaymentPlanForVariant(variantId) {
      const plan = this.fullPaymentPlanByVariant[String(variantId)] || {};
      this.fullPaymentSellingPlanId = plan.sellingPlanId ? String(plan.sellingPlanId) : '';
      this.currentFullPaymentPrice = Number(plan.price ?? this.currentVariantPrice);
      this.root.dataset.currentFullPaymentSellingPlanId = this.fullPaymentSellingPlanId;
    }

    isCurrentVariantSellable() {
      return Boolean(
        this.currentVariantId &&
          this.currentVariantAvailable &&
          this.fullPaymentSellingPlanId &&
          Number(this.currentFullPaymentPrice || 0) > 0
      );
    }

    selectedItems() {
      if (!this.isCurrentVariantSellable()) return [];

      const preorderProperties = this.preorderProperties(this.createKitId());

      return [
        {
          id: this.currentVariantId,
          quantity: 1,
          ...(this.currentSellingPlanId ? { selling_plan: this.currentSellingPlanId } : {}),
          properties: preorderProperties,
        },
        ...this.selectedAccessories().map((item) => ({
          id: item.id,
          quantity: item.quantity,
          ...(item.selling_plan ? { selling_plan: item.selling_plan } : {}),
          properties: preorderProperties,
        })),
      ];
    }

    preorderProperties(kitId) {
      return {
        _calinfi_preorder_payment: 'Full payment',
        _calinfi_preorder_terms: 'Accepted',
        _calinfi_estimated_shipping: '8–10 weeks',
        _calinfi_kit_id: kitId,
      };
    }

    createKitId() {
      if (typeof window.crypto?.randomUUID === 'function') return window.crypto.randomUUID();

      const randomPart = Math.random().toString(36).slice(2, 12);
      return `calinfi-${Date.now().toString(36)}-${randomPart}`;
    }

    totalPrice() {
      return this.selectedAccessories().reduce(
        (total, item) => total + item.price * item.quantity,
        Number(this.currentFullPaymentPrice || 0)
      );
    }

    pendingPriceLabel() {
      const priceText = this.root.querySelector('.calinfi-product-hero__price')?.textContent?.trim();
      return priceText || 'Price coming soon';
    }

    updateTotal() {
      const total = this.totalPrice();
      if (this.totalElement) {
        this.totalElement.textContent = this.isCurrentVariantSellable() ? this.formatMoney(total) : this.pendingPriceLabel();
      }
      this.updateMobileKitLabel();
      this.updatePaymentSummary(total);
      this.publishPurchaseState(total);
    }

    updateMobileKitLabel() {
      if (!this.mobileKitLabel) return;

      const accessoryQuantity = this.selectedAccessories().reduce((sum, item) => sum + item.quantity, 0);
      const itemCount = (this.isCurrentVariantSellable() ? 1 : 0) + accessoryQuantity;
      this.mobileKitLabel.textContent = `Total (${itemCount})`;
      if (this.root.classList.contains('is-mobile-kit-expanded')) this.renderMobileKitPanel();
    }

    updateButtons() {
      const disabled = !this.isCurrentVariantSellable();
      [this.addToCartButton, this.buyNowButton].forEach((button) => {
        if (!button) return;
        button.disabled = disabled;
        button.toggleAttribute('aria-disabled', disabled);
      });
      this.publishPurchaseState();
    }

    publishPurchaseState(total = this.totalPrice()) {
      const sourceLabel = this.root.querySelector('.calinfi-product-hero__total-label');
      const activeImage = this.root.querySelector('.calinfi-product-hero__media.is-active img');
      const productTitle = this.root.querySelector('.calinfi-product-hero__title')?.textContent?.trim() || '';

      document.dispatchEvent(
        new CustomEvent('calinfi:product-purchase:update', {
          detail: {
            label: sourceLabel?.textContent?.trim() || '',
            total: this.isCurrentVariantSellable() ? this.formatMoney(total) : this.pendingPriceLabel(),
            totalCents: total,
            product: {
              id: this.currentVariantId,
              title: productTitle,
              quantity: 1,
              price: Number(this.currentFullPaymentPrice || 0),
              image: activeImage?.currentSrc || activeImage?.src || '',
              selling_plan: this.currentSellingPlanId,
              purchase_option: this.selectedPaymentOption(),
            },
            accessories: this.selectedAccessories(),
            purchaseOption: this.selectedPaymentOption(),
            variantId: this.currentVariantId,
            available: this.isCurrentVariantSellable(),
          },
        })
      );
    }

    setBusy(isBusy) {
      [this.addToCartButton, this.buyNowButton].forEach((button) => {
        if (!button) return;
        button.classList.toggle('is-loading', isBusy);
        button.toggleAttribute('aria-disabled', isBusy || button.disabled);
      });
    }

    showError(message, { focus = false } = {}) {
      if (!this.errorElement) return;
      this.errorElement.textContent = message || window.cartStrings?.error || 'Something went wrong. Please try again.';
      this.errorElement.hidden = false;
      if (focus) window.requestAnimationFrame(() => this.errorElement?.focus());
    }

    clearError() {
      if (!this.errorElement) return;
      this.errorElement.textContent = '';
      this.errorElement.hidden = true;
    }

    async addItems({ checkout }) {
      this.updateCurrentSellingPlan();

      if (!this.validatePaymentSelection()) return;

      const items = this.selectedItems();
      if (!items.length) {
        this.showError(window.variantStrings?.unavailable || 'This product is unavailable.', { focus: true });
        return;
      }

      this.clearError();
      let kitConflictAction = 'add';

      try {
        kitConflictAction = await this.resolveKitConflict();
      } catch (error) {
        this.showError(error.message, { focus: true });
        return;
      }

      if (kitConflictAction === 'cancel') return;

      this.setBusy(true);

      const cartDrawer = document.querySelector('cart-drawer');
      cartDrawer?.setActiveElement?.(this.activeElement || document.activeElement);

      try {
        if (kitConflictAction === 'replace') {
          await this.removeExistingKitItems();
        }

        const addState = await this.addItemsWithJson(items, cartDrawer);
        const parsedState = checkout ? addState : await this.refreshCartState(cartDrawer, addState);

        if (parsedState.requiresCartPageFallback) {
          window.location.href = window.routes?.cart_url || '/cart';
          return;
        }

        try {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'calinfi-product-hero',
            productVariantId: this.currentVariantId,
            cartData: parsedState,
          });
        } catch (eventError) {
          console.warn('CALINFI cart update event failed', eventError);
        }

        if (checkout) {
          window.location.href = '/checkout';
          return;
        }

        if (cartDrawer) {
          await this.openCartDrawer(cartDrawer, parsedState);
          return;
        }

        window.location.href = window.routes?.cart_url || '/cart';
      } catch (error) {
        this.showError(error.message, { focus: true });
      } finally {
        this.setBusy(false);
      }
    }

    validatePaymentSelection() {
      if (!this.fullPaymentSellingPlanId) {
        const message = 'The full-payment preorder is not ready for this item yet.';
        const paymentModalIsOpen = Boolean(this.paymentModal && !this.paymentModal.hidden);
        this.showError(message, { focus: !paymentModalIsOpen });
        this.showPaymentError(message, { focus: paymentModalIsOpen });
        return false;
      }

      const selectedAccessoryCards = this.accessoryCards.filter((card) => Number(card.dataset.quantity || 0) > 0);
      const missingAccessoryPlan = selectedAccessoryCards.find((card) => !this.accessorySellingPlanId(card));

      if (missingAccessoryPlan) {
        const title = missingAccessoryPlan.dataset.title || 'one selected accessory';
        const paymentLabel = this.selectedPaymentLabel();
        const message = `${paymentLabel} is not ready for ${title}. Please remove that accessory and try again.`;
        const paymentModalIsOpen = Boolean(this.paymentModal && !this.paymentModal.hidden);
        this.showError(message, { focus: !paymentModalIsOpen });
        this.showPaymentError(message, { focus: paymentModalIsOpen });
        return false;
      }

      this.clearPaymentError();
      return true;
    }

    async resolveKitConflict() {
      const cart = await this.fetchCart();
      const existingStoves = this.findExistingStoveItems(cart);

      if (!existingStoves.length) return 'add';

      const currentHandle = this.currentProductHandle();
      const hasDifferentStove = existingStoves.some((item) => {
        const itemHandle = this.cartItemHandle(item);
        if (currentHandle && itemHandle) return itemHandle !== currentHandle;

        return Number(item.variant_id || item.id) !== this.currentVariantId;
      });
      if (!hasDifferentStove) return 'add';

      return this.requestKitConflictChoice(existingStoves);
    }

    async fetchCart() {
      const response = await fetch(`${window.routes?.cart_url || '/cart'}.js`, {
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      return this.parseCartResponse(response, new Error('We could not check your cart. Please try again.'));
    }

    findExistingStoveItems(cart) {
      const items = Array.isArray(cart?.items) ? cart.items : [];

      return items.filter((item) => this.isMainStoveCartItem(item));
    }

    isMainStoveCartItem(item) {
      const handle = this.cartItemHandle(item);
      if (['odun', 'odun-s', 'odun-ls'].includes(handle)) return true;

      const title = String(item.product_title || item.title || '')
        .trim()
        .toLowerCase();

      return ['odun', 'odun s', 'odun ls'].includes(title);
    }

    cartItemHandle(item) {
      if (item.handle) return String(item.handle).replace(/^\/+|\/+$/g, '').toLowerCase();

      const productUrl = String(item.url || '').split('?')[0];
      const match = productUrl.match(/\/products\/([^/]+)/);

      return match ? match[1].toLowerCase() : '';
    }

    currentProductHandle() {
      const productUrl = String(this.productInfo?.dataset.url || window.location.pathname || '').split('?')[0];
      const match = productUrl.match(/\/products\/([^/]+)/);

      return match ? match[1].toLowerCase() : '';
    }

    requestKitConflictChoice(existingStoves) {
      return new Promise((resolve) => {
        const existingNames = this.formatExistingStoveNames(existingStoves);
        const currentName = this.currentProductTitle();
        const previouslyFocused = document.activeElement;
        const modal = document.createElement('div');

        modal.className = 'calinfi-kit-conflict';
        modal.setAttribute('role', 'presentation');
        modal.innerHTML = `
          <div class="calinfi-kit-conflict__overlay" data-calinfi-kit-conflict-close></div>
          <section
            class="calinfi-kit-conflict__dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="calinfi-kit-conflict-title"
            aria-describedby="calinfi-kit-conflict-copy"
            tabindex="-1"
          >
            <button class="calinfi-kit-conflict__close" type="button" data-calinfi-kit-conflict-close aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
            <p class="calinfi-kit-conflict__eyebrow">Cart check</p>
            <h2 id="calinfi-kit-conflict-title" class="calinfi-kit-conflict__title">
              You already have a stove kit in your cart.
            </h2>
            <p id="calinfi-kit-conflict-copy" class="calinfi-kit-conflict__copy">
              Your cart includes ${this.escapeHtml(existingNames)}. Replace it with ${this.escapeHtml(currentName)}, or add ${this.escapeHtml(currentName)} as another kit?
            </p>
            <div class="calinfi-kit-conflict__actions">
              <button class="calinfi-kit-conflict__button calinfi-kit-conflict__button--primary" type="button" data-calinfi-kit-conflict-action="replace">
                Replace Kit
              </button>
              <button class="calinfi-kit-conflict__button calinfi-kit-conflict__button--secondary" type="button" data-calinfi-kit-conflict-action="add">
                Add Another Kit
              </button>
            </div>
          </section>
        `;

        const complete = (choice) => {
          document.removeEventListener('keydown', handleKeydown);
          modal.remove();
          document.body.classList.remove('calinfi-kit-conflict-open');
          previouslyFocused?.focus?.();
          resolve(choice);
        };

        const handleKeydown = (event) => {
          if (event.key === 'Escape') complete('cancel');
        };

        modal.addEventListener('click', (event) => {
          const action = event.target.closest('[data-calinfi-kit-conflict-action]')?.dataset.calinfiKitConflictAction;
          if (action) {
            complete(action);
            return;
          }

          if (event.target.closest('[data-calinfi-kit-conflict-close]')) {
            complete('cancel');
          }
        });

        document.addEventListener('keydown', handleKeydown);
        document.body.append(modal);
        document.body.classList.add('calinfi-kit-conflict-open');
        window.requestAnimationFrame(() => {
          modal.querySelector('.calinfi-kit-conflict__dialog')?.focus();
        });
      });
    }

    formatExistingStoveNames(items) {
      const names = items
        .map((item) => String(item.product_title || item.title || 'ODUN kit').trim())
        .filter(Boolean);
      const uniqueNames = [...new Set(names)];

      if (uniqueNames.length <= 1) return uniqueNames[0] || 'an ODUN kit';
      if (uniqueNames.length === 2) return `${uniqueNames[0]} and ${uniqueNames[1]}`;

      return `${uniqueNames.slice(0, -1).join(', ')}, and ${uniqueNames[uniqueNames.length - 1]}`;
    }

    currentProductTitle() {
      return this.root.querySelector('.calinfi-product-hero__title')?.textContent?.trim() || 'this ODUN kit';
    }

    async removeExistingKitItems() {
      const cart = await this.fetchCart();
      const items = Array.isArray(cart?.items) ? cart.items : [];
      const stoveItems = items.filter((item) => this.isMainStoveCartItem(item));
      const stoveKitIds = new Set(
        stoveItems
          .map((item) => this.cartItemKitId(item))
          .filter(Boolean)
      );
      const updates = Object.fromEntries(
        items
          .filter((item) => {
            if (this.isMainStoveCartItem(item)) return true;
            const kitId = this.cartItemKitId(item);
            return Boolean(kitId && stoveKitIds.has(kitId));
          })
          .map((item) => item.key)
          .filter(Boolean)
          .map((key) => [key, 0])
      );

      if (!Object.keys(updates).length) return cart;

      const route = window.routes?.cart_update_url || '/cart/update';
      const updateUrl = route.endsWith('.js') ? route : `${route}.js`;
      const response = await fetch(updateUrl, {
        ...this.cartFetchConfig(),
        body: JSON.stringify({ updates }),
      });

      return this.parseCartResponse(response, new Error('We could not replace your cart kit. Please try again.'));
    }

    cartItemKitId(item) {
      const properties = item?.properties && typeof item.properties === 'object' ? item.properties : {};
      return String(properties._calinfi_kit_id || '').trim();
    }

    async addItemsWithJson(items, cartDrawer = null) {
      const addUrl = this.cartAddUrl();
      const sections = this.cartDrawerSectionIds(cartDrawer);
      const response = await fetch(addUrl, {
        ...this.cartFetchConfig(),
        body: JSON.stringify({
          items,
          ...(sections.length ? { sections, sections_url: window.location.pathname } : {}),
        }),
      });

      return this.parseCartResponse(response);
    }

    async refreshCartState(cartDrawer, addState) {
      try {
        const [cart, sections] = await Promise.all([
          this.fetchCart(),
          cartDrawer ? this.fetchCartDrawerSections(cartDrawer) : Promise.resolve(addState.sections || null),
        ]);

        return {
          ...cart,
          id: addState.id || this.currentVariantId,
          sections: sections || addState.sections,
        };
      } catch (error) {
        return {
          ...addState,
          requiresCartPageFallback: true,
        };
      }
    }

    async openCartDrawer(cartDrawer, parsedState) {
      try {
        let sections = parsedState.sections;

        if (!this.hasCartDrawerSections(cartDrawer, sections)) {
          sections = await this.fetchCartDrawerSections(cartDrawer);
        }

        if (typeof cartDrawer.renderContents === 'function' && this.hasCartDrawerSections(cartDrawer, sections)) {
          cartDrawer.renderContents({
            ...parsedState,
            id: parsedState.id || this.currentVariantId,
            sections,
          });
          return;
        }
      } catch (drawerError) {
        console.warn('CALINFI cart drawer refresh failed', drawerError);
      }

      cartDrawer.open?.(this.activeElement || document.activeElement);
    }

    cartDrawerSectionIds(cartDrawer) {
      if (!cartDrawer || typeof cartDrawer.getSectionsToRender !== 'function') return [];

      return cartDrawer
        .getSectionsToRender()
        .map((section) => section.id)
        .filter(Boolean);
    }

    hasCartDrawerSections(cartDrawer, sections) {
      const sectionIds = this.cartDrawerSectionIds(cartDrawer);

      return Boolean(
        sections &&
          sectionIds.length &&
          sectionIds.every((sectionId) => typeof sections[sectionId] === 'string' && sections[sectionId].length)
      );
    }

    async addItemsSequentially(items) {
      let parsedState = null;

      for (let index = 0; index < items.length; index += 1) {
        const item = items[index];
        const body = {
          id: item.id,
          quantity: item.quantity,
          ...(item.selling_plan ? { selling_plan: item.selling_plan } : {}),
          ...(item.properties ? { properties: item.properties } : {}),
        };

        parsedState = await this.addSingleItemWithJson(body);
      }

      return parsedState;
    }

    async fetchCartDrawerSections(cartDrawer) {
      const sections = cartDrawer.getSectionsToRender?.().map((section) => section.id) || [
        'cart-drawer',
        'cart-icon-bubble',
      ];
      const url = new URL(window.location.href);
      url.hash = '';
      url.searchParams.delete('sections');
      url.searchParams.set('sections', sections.join(','));
      const response = await fetch(url.toString(), {
        cache: 'no-store',
        headers: {
          Accept: 'application/json',
          'Cache-Control': 'no-cache',
        },
      });
      const parsedState = await this.parseCartResponse(response);

      if (!sections.every((section) => parsedState[section])) {
        throw new Error(window.cartStrings?.error || 'We could not refresh the cart.');
      }

      return parsedState;
    }

    async addSingleItemWithJson(body) {
      const addUrl = this.cartAddUrl();
      const response = await fetch(addUrl, {
        ...this.cartFetchConfig(),
        body: JSON.stringify(body),
      });

      return this.parseCartResponse(response);
    }

    cartAddUrl() {
      const route = window.routes?.cart_add_url || '/cart/add';
      return route.endsWith('.js') ? route : `${route}.js`;
    }

    cartFetchConfig() {
      return {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
      };
    }

    async parseCartResponse(response, fallbackError = null) {
      const responseText = await response.text();
      let parsedState = {};

      if (responseText) {
        try {
          parsedState = JSON.parse(responseText);
        } catch (error) {
          throw new Error(this.readNonJsonCartError(responseText, fallbackError));
        }
      }

      if (response.status === 429 || parsedState.status === 'too_many_requests') {
        throw new Error('Too many cart attempts. Please wait a few minutes and try again.');
      }

      if (!response.ok || parsedState.status) {
        throw new Error(
          parsedState.description ||
            parsedState.message ||
            fallbackError?.message ||
            window.cartStrings?.error ||
            'We could not add these items to your cart.'
        );
      }

      return parsedState;
    }

    readNonJsonCartError(responseText, fallbackError = null) {
      const text = responseText || '';
      if (/rate limited|Error 1015|Verifying your connection/i.test(text)) {
        return 'Shopify is temporarily limiting cart requests. Please wait a few minutes and try again.';
      }

      return fallbackError?.message || window.cartStrings?.error || 'We could not update the cart.';
    }

    escapeHtml(value) {
      return String(value || '').replace(/[&<>"']/g, (character) => {
        const entities = {
          '&': '&amp;',
          '<': '&lt;',
          '>': '&gt;',
          '"': '&quot;',
          "'": '&#39;',
        };
        return entities[character];
      });
    }

    readJsonData(selector) {
      const element = this.root.querySelector(selector);
      if (!element?.textContent) return {};

      try {
        const parsed = JSON.parse(element.textContent);
        return parsed && typeof parsed === 'object' ? parsed : {};
      } catch (error) {
        console.warn(`[CALINFI] Invalid JSON data for ${selector}`, error);
        return {};
      }
    }

    focusableElements(container) {
      if (!container) return [];

      return Array.from(
        container.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((element) => !element.hidden && element.getAttribute('aria-hidden') !== 'true');
    }

    formatMoney(cents) {
      const value = Number(cents || 0);
      const format = this.moneyFormat;
      const placeholder = format.match(/\{\{\s*(\w+)\s*\}\}/);

      if (!placeholder) return `$${(value / 100).toFixed(2)}`;

      let formatted;
      switch (placeholder[1]) {
        case 'amount_no_decimals':
          formatted = this.formatWithDelimiters(value, 0);
          break;
        case 'amount_with_comma_separator':
          formatted = this.formatWithDelimiters(value, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          formatted = this.formatWithDelimiters(value, 0, '.', ',');
          break;
        case 'amount':
        default:
          formatted = this.formatWithDelimiters(value, 2);
          break;
      }

      return format.replace(placeholder[0], formatted);
    }

    formatWithDelimiters(cents, precision = 2, thousands = ',', decimal = '.') {
      const amount = (Number(cents || 0) / 100).toFixed(precision);
      const parts = amount.split('.');
      const dollars = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      const centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    }
  }

  const init = (container = document) => {
    if (container === document) {
      ensurePreorderView();
      installCartDrawerPreviewGuard();
      installMobileKitSummaryFallback();
    }
    container.querySelectorAll(SELECTOR).forEach((root) => new CalinfiProductHero(root).init());
  };

  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
